import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAssignmentsById, postAssignmentsSubmitKeyed } from '@/api/student';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { FileList, FileUpload } from '@/components/ui/FileUpload';
import { Banner, PageHeader, QueryState, TextArea } from '@/components/kit/ui';
import { errMsg, fmtDateTime, unwrap } from '@/components/kit/data';
import { newKey } from '../shared';

interface Att { name: string; url: string; size?: number }
interface Detail {
  id: number; title: string; subject: string | null; due_at: string | null; max_score: number | null; closed: boolean; state: string; score: number | null;
  instructions: string | null; attachments: Att[] | null;
  submission: { body: string | null; attachments: Att[] | null; submitted_at: string | null; feedback: string | null } | null;
}

export function AssignmentDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['student-assignment', id], queryFn: () => getAssignmentsById(id).then((r) => unwrap<Detail>(r)) });
  const a = q.data;
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<Att[]>([]);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (a?.submission) { setBody(a.submission.body ?? ''); setFiles(a.submission.attachments ?? []); }
  }, [a?.submission]);

  const submit = useMutation({
    mutationFn: () => postAssignmentsSubmitKeyed(id, { body: body.trim() || null, attachments: files.map((f) => ({ name: f.name.slice(0, 120), url: f.url })) }, newKey()),
    onSuccess: (r) => {
      setMsg({ tone: 'success', text: (r.data as { message?: string })?.message ?? 'Submitted.' });
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['student-assignment', id] });
      qc.invalidateQueries({ queryKey: ['student-assignments'] });
      qc.invalidateQueries({ queryKey: ['student-home'] });
    },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });

  const graded = a?.state === 'graded';
  const canSubmit = !!a && !a.closed && !graded;
  const showForm = canSubmit && (!a?.submission || editing);

  return (
    <div className="space-y-4">
      <PageHeader title={a?.title ?? 'Assignment'} subtitle={a ? `${a.subject ?? ''}${a.due_at ? ` · due ${fmtDateTime(a.due_at)}` : ''}` : undefined} action={<Link to="/student/learn" className="text-sm font-semibold text-brand-600">Back to assignments</Link>} />
      {msg && <Banner tone={msg.tone}>{msg.text}</Banner>}
      <QueryState q={q}>
        {a && (
          <>
            <Card title="Instructions" action={<StatusBadge status={a.state} />}>
              <p className="text-sm whitespace-pre-wrap">{a.instructions || 'No instructions were given.'}</p>
              {!!a.attachments?.length && <div className="mt-3"><FileList files={a.attachments} /></div>}
              {a.max_score ? <p className="text-xs text-slate-500 mt-3">Marked out of {a.max_score}.</p> : null}
              {a.closed && !a.submission && <p className="text-sm text-red-700 mt-3">This assignment is closed.</p>}
            </Card>

            {a.submission && !editing && (
              <Card title="Your submission" action={canSubmit ? <Button variant="ghost" onClick={() => setEditing(true)}>Edit and resubmit</Button> : undefined}>
                <p className="text-xs text-slate-500 mb-2">Submitted {fmtDateTime(a.submission.submitted_at)}</p>
                {a.submission.body && <p className="text-sm whitespace-pre-wrap">{a.submission.body}</p>}
                {!!a.submission.attachments?.length && <div className="mt-3"><FileList files={a.submission.attachments} /></div>}
                {graded && (
                  <div className="mt-4 border-t pt-3 text-sm">
                    <p>Score: <b>{a.score}{a.max_score ? ` / ${a.max_score}` : ''}</b></p>
                    {a.submission.feedback && <p className="mt-1"><span className="text-slate-500">Teacher's feedback:</span> {a.submission.feedback}</p>}
                  </div>
                )}
              </Card>
            )}

            {showForm && (
              <Card title={a.submission ? 'Resubmit your work' : 'Submit your work'}>
                <TextArea label="Your answer" rows={8} value={body} onChange={(e) => setBody(e.target.value)} maxLength={20000} />
                <div className="mt-3 space-y-2">
                  <FileList files={files} onRemove={(i) => setFiles((f) => f.filter((_, j) => j !== i))} />
                  {files.length < 10 && <FileUpload label="Attach a file" relatedType="assignment_submission" onUploaded={(f) => setFiles((p) => [...p, { name: f.name, url: f.url, size: f.size }])} />}
                </div>
                <div className="flex gap-2 justify-end mt-4">
                  {editing && <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>}
                  <Button disabled={submit.isPending || (!body.trim() && !files.length)} onClick={() => submit.mutate()}>{submit.isPending ? 'Submitting…' : 'Submit'}</Button>
                </div>
                {a.due_at && new Date(a.due_at).getTime() < Date.now() && <p className="text-xs text-amber-700 mt-2">The due date has passed; your work will be marked late.</p>}
              </Card>
            )}
          </>
        )}
      </QueryState>
    </div>
  );
}
